import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"

const linkButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap " +
  "font-[var(--font-family--sans)] font-semibold " +
  "tracking-[var(--letter-spacing-default)] " +
  "text-[var(--general-text-secondary)] " +
  "underline decoration-solid " +
  "bg-transparent " +
  "transition-all outline-none cursor-pointer " +
  "disabled:pointer-events-none disabled:opacity-50 " +
  "focus:bg-[var(--button-ghost-bg-default)] " +
  "focus:shadow-[0_0_0_3px_var(--focus-ring)]",
  {
    variants: {
      variant: {
        // Border-radius on focus handled by compoundVariants
        default: "",
        round:   "",
      },
      size: {
        // min-h: --semantic-3xl | py: --semantic-xs | px: --semantic-xl | gap: --semantic-xs | text: --font-size-s
        large:
          "min-h-[var(--semantic-3xl)] " +
          "py-[var(--semantic-xs)] px-[var(--semantic-xl)] " +
          "gap-[var(--semantic-xs)] " +
          "text-sm leading-[var(--line-height-s-heading)]",

        // min-h: --absolute-9 | py: --semantic-xs | px: --semantic-md | gap: --semantic-xs | text: --font-size-s
        default:
          "min-h-[var(--absolute-9)] " +
          "py-[var(--semantic-xs)] px-[var(--semantic-md)] " +
          "gap-[var(--semantic-xs)] " +
          "text-sm leading-[var(--line-height-s-heading)]",

        // min-h: --semantic-2xl | py: --semantic-2xs | px: --absolute-3 | gap: --semantic-xs | text: --font-size-s
        small:
          "min-h-[var(--semantic-2xl)] " +
          "py-[var(--semantic-2xs)] px-[var(--absolute-3)] " +
          "gap-[var(--semantic-xs)] " +
          "text-sm leading-[var(--line-height-s-heading)]",

        // min-h: --semantic-xl | py: --semantic-2xs | px: --semantic-xs | gap: --semantic-xs | text: --font-size-xs
        mini:
          "min-h-[var(--semantic-xl)] " +
          "py-[var(--semantic-2xs)] px-[var(--semantic-xs)] " +
          "gap-[var(--semantic-xs)] " +
          "text-xs leading-[var(--line-height-xs)]",
      },
    },
    compoundVariants: [
      // default variant: large/default/small → --semantic-rounded-lg on focus
      { variant: "default", size: "large",   class: "focus:rounded-[var(--semantic-rounded-lg)]" },
      { variant: "default", size: "default", class: "focus:rounded-[var(--semantic-rounded-lg)]" },
      { variant: "default", size: "small",   class: "focus:rounded-[var(--semantic-rounded-lg)]" },
      // default variant: mini → --semantic-rounded-sm on focus
      { variant: "default", size: "mini",    class: "focus:rounded-[var(--semantic-rounded-sm)]" },
      // round variant: all sizes → --semantic-rounded-full on focus
      { variant: "round",   size: "large",   class: "focus:rounded-[var(--semantic-rounded-full)]" },
      { variant: "round",   size: "default", class: "focus:rounded-[var(--semantic-rounded-full)]" },
      { variant: "round",   size: "small",   class: "focus:rounded-[var(--semantic-rounded-full)]" },
      { variant: "round",   size: "mini",    class: "focus:rounded-[var(--semantic-rounded-full)]" },
    ],
    defaultVariants: {
      variant: "default",
      size:    "default",
    },
  }
)

export interface LinkButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof linkButtonVariants> {
  asChild?:   boolean
  leftIcon?:  React.ReactNode
  rightIcon?: React.ReactNode
}

const LinkButton = React.forwardRef<HTMLButtonElement, LinkButtonProps>(
  ({ className, variant, size, asChild = false, leftIcon, rightIcon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(linkButtonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {leftIcon && (
          <span className="inline-flex items-center justify-center flex-shrink-0 w-[var(--semantic-md)] h-[var(--semantic-md)] text-[var(--general-text-secondary)] [&>svg]:w-full [&>svg]:h-full">
            {leftIcon}
          </span>
        )}
        {children}
        {rightIcon && (
          <span className="inline-flex items-center justify-center flex-shrink-0 w-[var(--semantic-md)] h-[var(--semantic-md)] text-[var(--general-text-secondary)] [&>svg]:w-full [&>svg]:h-full">
            {rightIcon}
          </span>
        )}
      </Comp>
    )
  }
)
LinkButton.displayName = "LinkButton"

export { LinkButton, linkButtonVariants }
